import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EhiDirection, EhiDirectionScores, EhiDirectionsBreakdown, EhiMatrixBlock } from '@services/ehi.service.ts';

const BLOCKS: { key: EhiMatrixBlock; labelKey: string }[] = [
  { key: 'A', labelKey: 'ehiJournal.blockActivity' },
  { key: 'F', labelKey: 'ehiJournal.blockFeeling' },
  { key: 'T', labelKey: 'ehiJournal.blockTasks' },
  { key: 'N', labelKey: 'ehiJournal.blockNutrition' },
  { key: 'W', labelKey: 'ehiJournal.blockWater' },
];

const DIRECTION_LABEL_KEYS: Record<EhiDirection, string> = {
  physical: 'ehiJournal.directionPhysical',
  energy: 'ehiJournal.directionEnergy',
  clarity: 'ehiJournal.directionClarity',
  productivity: 'ehiJournal.directionProductivity',
  focus: 'ehiJournal.directionFocus',
  emotional: 'ehiJournal.directionEmotional',
};

const cellSx = { px: 1, py: 0.75, whiteSpace: 'nowrap', verticalAlign: 'top' } as const;

interface Props {
  breakdown: EhiDirectionsBreakdown;
  /** Stored direction scores of the snapshot, compared with the computed ones. */
  stored: EhiDirectionScores | null;
}

/**
 * The 6 directions as the matrix computes them: coefficient per block (as in the config matrix),
 * block contribution under it, row result and the stored value for comparison.
 */
export const EhiDirectionsMatrixTable: React.FC<Props> = ({ breakdown, stored }) => {
  const { t, i18n } = useTranslation();
  const format = (value: number, digits: number) =>
    value.toLocaleString(i18n.language, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const isTApplicable = breakdown.blocks.T !== null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="caption" color="text.secondary">{t('ehiJournal.matrixFormula')}</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={cellSx}>{t('ehiJournal.colDirection')}</TableCell>
              {BLOCKS.map((b) => (
                <TableCell key={b.key} align="right" sx={cellSx}>{`${t(b.labelKey)} (${b.key})`}</TableCell>
              ))}
              <TableCell align="right" sx={cellSx}>{t('ehiJournal.colComputed')}</TableCell>
              <TableCell align="right" sx={cellSx}>{t('ehiJournal.colStoredValue')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellSx, color: 'text.secondary' }}>{t('ehiJournal.rowBlockScore')}</TableCell>
              {BLOCKS.map((b) => {
                const score = breakdown.blocks[b.key];
                return (
                  <TableCell key={b.key} align="right" sx={{ ...cellSx, fontWeight: 600 }}>
                    {score === null ? '—' : format(score, 1)}
                  </TableCell>
                );
              })}
              <TableCell sx={cellSx} />
              <TableCell sx={cellSx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {breakdown.rows.map((row) => {
              const storedValue = stored?.[row.direction];
              const differs = storedValue !== undefined && Math.abs(storedValue - row.value) > 0.01;
              return (
                <TableRow key={row.direction}>
                  <TableCell sx={cellSx}>{t(DIRECTION_LABEL_KEYS[row.direction])}</TableCell>
                  {BLOCKS.map((b) => {
                    const applied = row.appliedWeights[b.key];
                    const score = breakdown.blocks[b.key];
                    if (applied === null || score === null) {
                      return (
                        <TableCell key={b.key} align="right" sx={{ ...cellSx, color: 'text.disabled' }}>
                          {format(row.weights[b.key], 2)}
                          <Typography variant="caption" display="block">—</Typography>
                        </TableCell>
                      );
                    }
                    const renormalised = applied !== row.weights[b.key];
                    return (
                      <TableCell key={b.key} align="right" sx={cellSx}>
                        {format(applied, renormalised ? 3 : 2)}
                        {renormalised && (
                          <Typography component="span" variant="caption" color="text.secondary">{` (${format(row.weights[b.key], 2)})`}</Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {`= ${format(applied * score, 2)}`}
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" sx={{ ...cellSx, fontWeight: 600 }}>{format(row.value, 2)}</TableCell>
                  <TableCell align="right" sx={{ ...cellSx, color: differs ? 'warning.main' : undefined }}>
                    {storedValue === undefined ? '—' : format(storedValue, 2)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {!isTApplicable && <Typography variant="caption" color="text.secondary">{t('ehiJournal.matrixTNotApplicable')}</Typography>}
      <Typography variant="caption" color="text.secondary">
        {t(breakdown.matrixSource === 'stored' ? 'ehiJournal.matrixStored' : 'ehiJournal.matrixCurrent')}
      </Typography>
    </Box>
  );
};
